import { createFileRoute, Link } from "@tanstack/react-router";
import { Info, OctagonAlert, X } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { z } from "zod";

import icon from "@/assets/images/icon.png";
import { LoginForm } from "@/features/auth/modules/login/login-form";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

type AuthToastVariant = "error" | "warning";

// Custom toast so the OAuth error reads like a proper alert: top-center, longer
// on screen, and styled with semantic tokens. The fixed `id` dedupes the
// React StrictMode double-mount in dev so it only shows once.
function notifyAuthError(variant: AuthToastVariant, title: string, description: string) {
  const Icon = variant === "error" ? OctagonAlert : Info;

  toast.custom(
    (id) => (
      <div className="bg-card relative flex w-full items-start gap-3 overflow-hidden rounded-xl border p-4 pl-5 shadow-lg">
        <span className={cn("absolute inset-y-0 left-0 w-1", variant === "error" ? "bg-destructive" : "bg-primary")} />
        <div
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-full",
            variant === "error" ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary",
          )}
        >
          <Icon className="size-5" />
        </div>
        <div className="flex-1 space-y-0.5">
          <p className="text-foreground text-sm font-semibold">{title}</p>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>
        <button
          type="button"
          onClick={() => toast.dismiss(id)}
          aria-label={m.auth_toast_dismiss()}
          className="text-muted-foreground hover:text-foreground -mt-1 -mr-1 rounded-md p-1 transition-colors"
        >
          <X className="size-4" />
        </button>
      </div>
    ),
    { id: `auth-${variant}`, duration: 8000, position: "top-center" },
  );
}

export const Route = createFileRoute("/(public)/login/")({
  component: RouteComponent,
  loader: () => {
    return {
      crumb: m.auth_login_crumb(),
    };
  },
  validateSearch: z.object({
    redirect: z.string().optional(),
    invitation_token: z.string().optional(),
    error: z.string().optional(),
  }),
});

function RouteComponent() {
  const { invitation_token, redirect: redirectPath, error } = Route.useSearch();

  useEffect(() => {
    if (error === "oauth_no_account") {
      notifyAuthError(
        "warning",
        m.auth_login_toast_account_needed_title(),
        m.auth_login_toast_account_needed_description(),
      );
    } else if (error === "oauth_failed") {
      notifyAuthError("error", m.auth_login_toast_failed_title(), m.auth_login_toast_failed_description());
    }
  }, [error]);

  useEffect(() => {
    if (invitation_token) {
      sessionStorage.setItem("invitation_token", invitation_token);
    }
  }, [invitation_token]);

  useEffect(() => {
    if (redirectPath) {
      sessionStorage.setItem("auth_redirect_path", redirectPath);
    }
  }, [redirectPath]);

  return (
    <div className="relative flex min-h-svh items-center justify-center overflow-hidden bg-background px-6 py-10">
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-1/4 -top-1/3 size-[45rem] rounded-full bg-[radial-gradient(circle_at_center,var(--primary),transparent_60%)] opacity-30 blur-[140px]" />
        <div className="absolute -right-1/4 -bottom-1/4 size-[38rem] rounded-full bg-[radial-gradient(circle_at_center,var(--secondary),transparent_60%)] opacity-25 blur-[140px]" />
      </div>
      <div className="relative z-10 flex w-full max-w-xl flex-col items-center gap-8">
        <Link
          to="/"
          className="relative flex items-center justify-center w-16 h-16 border border-transparent rounded-2xl shadow-2xl [background:linear-gradient(var(--background),var(--background))_padding-box,conic-gradient(var(--primary),var(--secondary)_25%,var(--secondary)_75%,var(--primary)_100%)_border-box] before:absolute before:inset-0 before:bg-primary/10 before:rounded-2xl"
        >
          <div aria-hidden className="bg-primary/30 absolute inset-0 blur-2xl" />
          <img src={icon} alt="" className="relative size-8 rounded-2xl" />
        </Link>
        <div className="w-full max-w-sm">
          <LoginForm />
        </div>
      </div>
    </div>
  );
}
